import { Stripe, loadStripe as loadStripeJs } from '@stripe/stripe-js';
import { fetchClient } from 'src/models/client/client';
import { Coupon } from 'src/models/coupon/_types';
import { fetchGroup } from 'src/models/group/group';
import { fetchPackage } from 'src/models/package/package';
import { fetchProduct, fetchProductVariant } from 'src/models/product/product';
import { Session } from 'src/models/session/_types';
import { fetchTrainerByTrainerId } from 'src/models/trainer/trainer';
import { User } from 'src/models/user/_types';
import { fetchUser } from 'src/models/user/user';
import { getUserDisplayName } from 'src/models/user/utils';
import { fetchWorkoutPlan } from 'src/models/workout-plan/workout-plan';
import { selectableItemTypes } from 'src/shared/constants/itemTypes';
import { Identifier } from 'src/types/utilityTypes';
import api from 'src/utils/ajax';
import { formatCurrency } from 'src/utils/numberFormatter';

import { Cart, CartItem, PaymentOption } from './_types';

export const loadStripe = async (session: Session, platformStripeAccount: boolean = false): Promise<Stripe | null> => {
  const params = {} as { stripeAccount?: string };
  let stripeToken = session.stripeToken || session.platformStripeToken;

  if (platformStripeAccount) {
    stripeToken = session.platformStripeToken;
  } else if (!session.stripeToken && !session.destinationCharges) {
    params.stripeAccount = session.stripeAccountId;
  }

  return loadStripeJs(stripeToken, params);
};

export const addItem = async (cart: Cart, newItem: CartItem): Promise<Cart> => {
  const existingItemIndex = cart.cartItems.findIndex(
    (item) =>
      item.itemId === newItem.itemId && item.itemType === newItem.itemType
  );

  let updatedCartItems;
  if (existingItemIndex !== -1) {
    // Replace existing item
    updatedCartItems = [
      ...cart.cartItems.slice(0, existingItemIndex),
      newItem,
      ...cart.cartItems.slice(existingItemIndex + 1),
    ];
  } else {
    // Add new item
    updatedCartItems = [...cart.cartItems, newItem];
  }

  const updatedCart: Cart = {
    ...cart,
    cartItems: updatedCartItems,
  };

  return updatedCart;
};

export const removeItem = async (
  cart: Cart,
  itemToRemove: CartItem
): Promise<Cart> => {
  const updatedCartItems = cart.cartItems.filter(
    (item) =>
      !(
        item.itemId === itemToRemove.itemId &&
        item.itemType === itemToRemove.itemType
      )
  );

  const updatedCart: Cart = {
    ...cart,
    cartItems: updatedCartItems,
  };

  return updatedCart;
};

export const hasSubscriptionUpgrade = async (
  cart: Cart,
  purchasedItems: any[]
): Promise<boolean> => {
  const upgrades = await Promise.all(
    cart.cartItems.map(async (cartItem) => {
      return (
        !canBeMultiplePurchased(
          cartItem.itemId?.toString() || '',
          cartItem.itemType || ''
        ) &&
        (
          await hasPurchased(
            cartItem.itemId?.toString() || '',
            cartItem.itemType || '',
            purchasedItems
          )
        )?.id
      );
    })
  );

  return upgrades.some((upgrade) => upgrade);
};

export const validateItems = async (
  cart: Cart,
  purchasedItems: any[]
): Promise<any[]> => {
  const response = await api.post('/api/v4/carts/validate_items', { cart });
  const validatedItems = response.data;

  const removedItems: any[] = [];

  for (const item of validatedItems) {
    if (!item.valid) {
      const cartItem = cart.cartItems.find(
        (i) => i.itemId === item.item_id && i.itemType === item.item_type
      );
      if (cartItem) {
        cart = await removeItem(cart, cartItem);
        removedItems.push(item);
      }
    }
  }

  for (const cartItem of cart.cartItems) {
    if (
      !canBeMultiplePurchased(
        cartItem.itemId?.toString() || '',
        cartItem.itemType || ''
      )
    ) {
      const paymentOption = await hasPurchased(
        cartItem.itemId?.toString() || '',
        cartItem.itemType || '',
        purchasedItems
      );

      if (
        (paymentOption.id || paymentOption.amount) &&
        (paymentOption.type === 'purchase' ||
          cartItem.paymentOption?.type === 'purchase' ||
          paymentOption.isIap ||
          (paymentOption.id === cartItem.paymentOption?.id &&
            cartItem.paymentOption?.type === 'subscription'))
      ) {
        cart = await removeItem(cart, cartItem);
        removedItems.push({
          item_id: cartItem.itemId,
          item_type: cartItem.itemType,
          valid: false,
        });
      }
    }

    // TODO: Implement upsell validation
    // const hasUpgrade = await hasSubscriptionUpgrade(cart);
    // if (
    //   hasUpgrade &&
    //   cartItem.upsellId &&
    //   !removedItems.find(
    //     (i) => i.item_id === item.id && i.item_type === getItemType(item)
    //   )
    // ) {
    //   removedItems.push({
    //     item_id: item.id,
    //     item_type: getItemType(item),
    //     valid: false,
    //     reason: 'no_upsells',
    //   });
    // }
  }

  return removedItems;
};

export const cartTypeToItemType = (cartType?: string): string | undefined => {
  const itemType = selectableItemTypes.find(
    (itemType) => itemType.modelName === cartType
  );

  if (itemType) {
    return itemType.value;
  }

  return cartType;
};

const hasPurchased = async (
  itemId: string,
  itemType: string,
  purchasedItems: any[]
): Promise<any> => {
  return purchasedItems.find(
    (i) => i.item_id === itemId && i.item_type === itemType
  );
};

const canBeMultiplePurchased = (itemId: string, itemType: string): boolean => {
  return itemType === 'Fbm::Package' || itemType === 'Exercisecom::Product';
};

export const getPaymentOptionDetails = (
  paymentOption?: PaymentOption,
  includeName = true
): string => {
  if (!paymentOption) {
    return 'Free';
  }

  const details: string[] = [];

  const formatAmountFromCents = (amountInCents: number, currency: string) => {
    const amountInDollars = amountInCents / 100;
    return formatCurrency(amountInDollars, { currency });
  };

  const formattedAmount = formatAmountFromCents(
    paymentOption.amount,
    paymentOption.currency
  );

  if (paymentOption.type === 'purchase') {
    details.push(formattedAmount);
  } else if (paymentOption.type === 'subscription') {
    const intervalString =
      paymentOption.intervalCount === 1
        ? paymentOption.interval
        : `${paymentOption.intervalCount} ${paymentOption.interval}s`;
    details.push(`${formattedAmount}/${intervalString}`);

    if (paymentOption.name && includeName) {
      details.push(paymentOption.name);
    }

    if (paymentOption.trialPeriodDays) {
      details.push(`${paymentOption.trialPeriodDays} day trial`);
    }
  }

  if (paymentOption.isIap) {
    details.push('In-App Purchase');
  }

  if (paymentOption.location) {
    details.push(paymentOption.location);
  }

  return details.join(' • ');
};

export const isTerminalPaymentMethod = (cart: Cart) => {
  return (
    cart.paymentMethod &&
    typeof cart.paymentMethod === 'object' &&
    cart.paymentMethod.type === 'card_present'
  );
};

export interface FormatPaymentOptionOptions {
  /** If true, only show the interval description without the price */
  onlyInterval?: boolean;
  /** Currency to use for formatting (defaults to option.currency) */
  currency?: string;
}

/**
 * Formats a payment option for display
 * @param option - The payment option to format
 * @param t - Translation function (expects keys: interval.day, interval.week, interval.month, interval.year, intervalWithCount, oneTimePayment)
 * @param options - Formatting options
 * @returns Formatted string (e.g., "$49.99 / month", "every 2 weeks", "One-time payment")
 */
export const formatPaymentOptionDescription = (
  option: PaymentOption,
  t: (key: string, options?: Record<string, unknown>) => string,
  options: FormatPaymentOptionOptions = {}
): string => {
  const { onlyInterval = false, currency } = options;

  if (option.type === 'subscription' && option.interval) {
    const intervalLabel =
      option.intervalCount === 1
        ? t(`interval.${option.interval}`)
        : t('intervalWithCount', {
            count: option.intervalCount,
            interval: t(`interval.${option.interval}`),
          });

    if (onlyInterval) {
      return intervalLabel;
    }

    const price = formatCurrency(option.amount, {
      currency: currency || option.currency,
      cents: true,
    });
    return `${price} / ${intervalLabel}`;
  }

  // One-time payment
  if (onlyInterval) {
    return t('oneTimePayment');
  }

  const price = formatCurrency(option.amount, {
    currency: currency || option.currency,
    cents: true,
  });
  return price;
};

const defaultPaymentOption = (): PaymentOption => ({
  id: 'default',
  type: 'purchase',
  amount: 0,
  currency: 'usd',
  name: 'One Time Purchase',
  location: null,
});

const resolveHydratedPaymentOption = (
  itemType: string | undefined,
  cartItemItem: any,
  saved: PaymentOption | undefined | null,
  session: Session | null
): PaymentOption => {
  if (saved && saved.type && (saved.amount != null || saved.id)) {
    return saved;
  }
  if (cartItemItem?.paymentOptions?.length) {
    return cartItemItem.paymentOptions[0];
  }
  if (
    (itemType === 'ClientAssociation' || itemType === 'TrainerAssociation') &&
    session?.paymentOptions?.[0]
  ) {
    return session.paymentOptions[0];
  }
  return defaultPaymentOption();
};

const pickItemMetaFields = (row: CartItem) => {
  const m = row.itemMeta as
    | {
        userIds?: Identifier[];
        disableFee?: boolean;
        shareWithAllDependents?: boolean | number;
        backdateIsStartdate?: boolean;
        overrideProrationBehavior?: CartItem['overrideProrationBehavior'];
      }
    | undefined;

  return {
    userIds: m?.userIds ?? row.userIds,
    disableFee: m?.disableFee ?? row.disableFee,
    shareWithAllDependents: m?.shareWithAllDependents ?? row.shareWithAllDependents,
    backdateIsStartdate: m?.backdateIsStartdate ?? row.backdateIsStartdate,
    overrideProrationBehavior:
      m?.overrideProrationBehavior ?? row.overrideProrationBehavior,
  };
};

async function hydrateOneCartItem(
  row: CartItem,
  cart: Cart,
  session: Session | null,
  currentUserId?: Identifier
): Promise<CartItem | null> {
  const itemId = row.itemId;
  const itemType = row.itemType;
  if (itemId == null || !itemType) {
    return null;
  }

  let cartItemItem: any = null;
  try {
    switch (itemType) {
      case 'ClientAssociation':
        cartItemItem = await fetchClient({ id: itemId });
        break;
      case 'Group':
        cartItemItem = await fetchGroup({ id: itemId });
        break;
      case 'Fbm::Package':
        cartItemItem = await fetchPackage({ id: itemId });
        break;
      case 'Exercisecom::Product':
        cartItemItem = await fetchProduct({ id: itemId });
        break;
      case 'WorkoutPlan':
        cartItemItem = await fetchWorkoutPlan({ id: itemId });
        break;
      case 'TrainerAssociation':
        cartItemItem = await fetchTrainerByTrainerId({ trainerId: itemId });
        break;
      case 'ProductVariant':
        cartItemItem = await fetchProductVariant({ id: itemId });
        break;
      default:
        /* eslint-disable-next-line no-console */
        console.error('hydrateCartItems: unsupported itemType', itemType);
        return null;
    }
  } catch (error) {
    /* eslint-disable-next-line no-console */
    console.error('hydrateCartItems: fetch failed', itemType, itemId, error);
    return null;
  }

  if (
    (!cartItemItem.paymentOptions || cartItemItem.paymentOptions.length === 0) &&
    itemType !== 'ClientAssociation' &&
    itemType !== 'TrainerAssociation'
  ) {
    cartItemItem.paymentOptions = [defaultPaymentOption()];
  }

  const meta = pickItemMetaFields(row);
  const paymentOption = resolveHydratedPaymentOption(
    itemType,
    cartItemItem,
    row.paymentOption,
    session
  );

  let shareUsers = row.users;
  if (
    itemType === 'Fbm::Package' &&
    (!shareUsers || shareUsers.length === 0) &&
    meta.userIds &&
    meta.userIds.length > 0
  ) {
    const ids = [...new Set(meta.userIds.map((id) => Number(id)))].filter(
      (n) => !Number.isNaN(n)
    );
    const fetched = await Promise.all(
      ids.map(async (id) => {
        try {
          return await fetchUser({ id });
        } catch {
          return null;
        }
      })
    );
    const validUsers = fetched.filter((u): u is User => u != null);
    shareUsers = validUsers.map((u) => ({
      id: typeof u.id === 'string' ? parseInt(u.id, 10) : (u.id as number),
      name: getUserDisplayName(u),
    }));
    if (!shareUsers.length) {
      shareUsers = ids.map((id) => ({ id, name: '' }));
    }
  }

  const base: CartItem = {
    quantity: row.quantity ?? 1,
    userId: row.userId ?? cart.userId,
    item: cartItemItem,
    itemId,
    itemType,
    paymentOption,
    coupon: row.coupon,
    chargedById: row.chargedById ?? currentUserId,
    cancelDate: row.cancelDate,
    billingCycleAnchor: row.billingCycleAnchor,
    backdateIsStartdate: meta.backdateIsStartdate ?? row.backdateIsStartdate,
    overrideProrationBehavior:
      meta.overrideProrationBehavior ?? row.overrideProrationBehavior,
    activationDate: row.activationDate,
    expiresOn: row.expiresOn,
    startOn: row.startOn,
    backdate: row.backdate,
    primaryCommissioneeIds: row.primaryCommissioneeIds,
    secondaryCommissioneeIds: row.secondaryCommissioneeIds,
    editVisits: row.editVisits,
    userIds: meta.userIds,
    disableFee: meta.disableFee,
    shareWithAllDependents: meta.shareWithAllDependents,
    users: shareUsers ?? row.users,
  };

  return base;
}

/**
 * Rebuild in-memory cart rows from an API cart (e.g. retry failed payment).
 */
export const hydrateCartItems = async (
  cart: Cart,
  options: { session: Session | null; currentUserId?: Identifier }
): Promise<CartItem[]> => {
  const { session, currentUserId } = options;
  const rows = cart.cartItems || [];

  const items = await Promise.all(
    rows.map((row) => hydrateOneCartItem(row, cart, session, currentUserId))
  );

  return items.filter((item): item is CartItem => item != null);
};

// --- Cart item form: coupon / billing anchor normalization (e.g. PaymentDialog) ---

/** Yearly subscription billing-cycle anchor: calendar month (1–12). */
export const BILLING_MONTHS_YEARLY = [
  { label: 'January', value: 1 },
  { label: 'February', value: 2 },
  { label: 'March', value: 3 },
  { label: 'April', value: 4 },
  { label: 'May', value: 5 },
  { label: 'June', value: 6 },
  { label: 'July', value: 7 },
  { label: 'August', value: 8 },
  { label: 'September', value: 9 },
  { label: 'October', value: 10 },
  { label: 'November', value: 11 },
  { label: 'December', value: 12 },
] as const;

/** Weekly subscription billing-cycle anchor: day-of-week string sent to the API. */
export const BILLING_WEEKLY_ANCHORS = [
  { label: 'Sunday', value: 'sunday' },
  { label: 'Monday', value: 'monday' },
  { label: 'Tuesday', value: 'tuesday' },
  { label: 'Wednesday', value: 'wednesday' },
  { label: 'Thursday', value: 'thursday' },
  { label: 'Friday', value: 'friday' },
  { label: 'Saturday', value: 'saturday' },
] as const;

/** Non-weekly, non-yearly anchors: day-of-month (1–28) plus end-of-month (-1). */
export function monthDayAnchors() {
  return [
    { label: '1st', value: 1 },
    { label: '2nd', value: 2 },
    { label: '3rd', value: 3 },
    ...Array.from({ length: 25 }, (_, i) => ({
      label: `${i + 4}th`,
      value: i + 4,
    })),
    { label: 'End of month', value: -1 },
  ];
}

/** Stripe-style override values; labels come from i18n via `clients.paymentDialog` keys. */
export const OVERRIDE_PRORATION_VALUE_TO_LABEL_KEY = {
  prorate: 'prorateFirstCharge',
  no_prorate: 'chargeFullAmount',
  no_charge: 'doNotCharge',
} as const;

export type OverrideProrationFormLabelKey =
  (typeof OVERRIDE_PRORATION_VALUE_TO_LABEL_KEY)[keyof typeof OVERRIDE_PRORATION_VALUE_TO_LABEL_KEY];

export const OVERRIDE_PRORATION_VALUES = [
  'prorate',
  'no_prorate',
  'no_charge',
] as const satisfies ReadonlyArray<keyof typeof OVERRIDE_PRORATION_VALUE_TO_LABEL_KEY>;

export function normalizeCouponForForm(
  coupon: CartItem['coupon'] | null | undefined
): CartItem['coupon'] | undefined {
  if (!coupon || typeof coupon !== 'object') {
    return undefined;
  }
  const c = coupon as Coupon & { type?: string; id?: string | number };
  if (!('code' in c) || !c.code) {
    return undefined;
  }
  const next = { ...c } as Coupon;
  const typeStr = String(next.type || '');
  if (typeStr === 'Subscription') {
    (next as { type: string }).type = 'subscription';
  } else if (typeStr === 'Purchase') {
    (next as { type: string }).type = 'purchase';
  }
  const rawId = (next as { id?: string | number }).id;
  if (typeof rawId === 'number') {
    (next as { id: string }).id = String(rawId);
  }
  return next;
}

export function coerceOverrideProrationBehavior(
  raw: CartItem['overrideProrationBehavior'] | string | null | undefined,
  labelFor: (key: OverrideProrationFormLabelKey) => string
): CartItem['overrideProrationBehavior'] | undefined {
  if (raw == null || raw === '') {
    return undefined;
  }
  if (typeof raw === 'object' && 'value' in raw && 'label' in raw) {
    return raw as CartItem['overrideProrationBehavior'];
  }
  if (typeof raw !== 'string') {
    return undefined;
  }
  if (!(raw in OVERRIDE_PRORATION_VALUE_TO_LABEL_KEY)) {
    return undefined;
  }
  const labelKey =
    OVERRIDE_PRORATION_VALUE_TO_LABEL_KEY[
      raw as keyof typeof OVERRIDE_PRORATION_VALUE_TO_LABEL_KEY
    ];
  return {
    value: raw,
    label: labelFor(labelKey),
  } as CartItem['overrideProrationBehavior'];
}

export function coerceBillingCycleAnchor(
  raw: CartItem['billingCycleAnchor'] | number | string | null | undefined,
  paymentOption: PaymentOption | undefined
): CartItem['billingCycleAnchor'] | undefined {
  if (raw == null || raw === '') {
    return undefined;
  }
  if (typeof raw === 'object' && 'value' in raw && 'label' in raw) {
    return raw as CartItem['billingCycleAnchor'];
  }
  if (!paymentOption) {
    return undefined;
  }
  if (paymentOption.interval === 'year') {
    const n = typeof raw === 'number' ? raw : parseInt(String(raw), 10);
    if (Number.isNaN(n)) {
      return undefined;
    }
    const found = BILLING_MONTHS_YEARLY.find((m) => m.value === n);
    return found as unknown as CartItem['billingCycleAnchor'];
  }
  if (paymentOption.interval === 'week') {
    const s = String(raw).toLowerCase();
    const found = BILLING_WEEKLY_ANCHORS.find((w) => w.value === s);
    return found as unknown as CartItem['billingCycleAnchor'];
  }
  const n = typeof raw === 'number' ? raw : parseInt(String(raw), 10);
  if (!Number.isNaN(n)) {
    const found = monthDayAnchors().find((d) => d.value === n);
    return found as unknown as CartItem['billingCycleAnchor'];
  }
  const s = String(raw).toLowerCase();
  const found = BILLING_WEEKLY_ANCHORS.find((w) => w.value === s);
  return found as unknown as CartItem['billingCycleAnchor'];
}
